import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { formatCurrencyInt, formatCurrency } from './formatters';
import { PROVEEDORES, areaTexto, areaCorta, conclusiones } from './liquidacion';

// Informe de liquidación para la gerencia (PDF) y su versión en Excel.
// Ambos salen de armarLiquidacion(): mismas cifras que la pantalla.

const AZUL = [15, 98, 254];
const MAGENTA = [208, 38, 112]; // Soporte en Sitio: bien distinto del azul de Mesa
const TINTA = [22, 22, 22];
const GRIS = [111, 111, 111];
const LINEA = [224, 224, 224];

const pct = (v, t) => (t ? `${((v / t) * 100).toFixed(1)}%` : '—');
const pesos = (n) => (n === null || n === undefined ? '—' : formatCurrencyInt(n));
const hoy = () => new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });

const METODOLOGIA = [
  ['Costo del proveedor', 'Tarifa mensual del contrato vigente de cada herramienta (TBBC, Optimize, AXD). Si la herramienta es de tarifa completa, cada cliente que la usa paga la tarifa; si es compartida, la tarifa se divide entre sus clientes.'],
  ['Costo Cloud', 'Consumo de IBM Cloud del mes (CSV de facturación). Cada recurso se asigna a la aplicación que lo usa; el costo de la aplicación se divide en partes iguales entre sus clientes. Los recursos comunes se reparten primero por igual entre todas las aplicaciones. Se convierte a pesos con la TRM del mes.'],
  ['Mesa de Servicio / Soporte en Sitio', 'Cada herramienta pertenece a un área. Las compartidas se dividen según su porcentaje (ej. 50/50).']
];

/* ============================== PDF ============================== */

export const generarInformePDF = ({ liq, nombreMes, onSuccess, onError }) => {
  try {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 40;
    const t = liq.totales;
    const sinTrm = !liq.trm;

    const encabezado = (titulo) => {
      doc.setFillColor(...AZUL);
      doc.rect(0, 0, W, 6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...GRIS);
      doc.text(`INFORME DE COSTOS I+D · ${nombreMes.toUpperCase()}`, M, 26);
      doc.setFontSize(15);
      doc.setTextColor(...TINTA);
      doc.text(titulo, M, 48);
      return 64;
    };

    // ---- Página 1: resumen ejecutivo ----
    doc.setFillColor(...AZUL);
    doc.rect(0, 0, W, 120, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('INFORME DE COSTOS I+D', M, 40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.text(nombreMes, M, 74);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Costo Cloud (IBM Cloud) + costo de proveedores, por cliente y por área', M, 98);

    // TRM destacada a la derecha del encabezado: de ella depende todo el costo Cloud en pesos
    const bx = W - M - 130;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(bx, 34, 130, 56, 4, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...GRIS);
    doc.text('TRM DEL MES', bx + 65, 52, { align: 'center' });
    doc.setFontSize(20);
    if (liq.trm) {
      doc.setTextColor(...AZUL);
      doc.text(`$${liq.trm.toLocaleString('es-CO')}`, bx + 65, 78, { align: 'center' });
    } else {
      doc.setTextColor(178, 34, 34);
      doc.setFontSize(13);
      doc.text('Sin TRM', bx + 65, 76, { align: 'center' });
    }

    let y = 156;
    doc.setTextColor(...TINTA);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Resumen', M, y);
    y += 14;

    // Tarjetas: total, Mesa, Sitio
    const tarjetas = [
      { label: 'Costo total', valor: pesos(t.total), sub: `${liq.clientes.length} clientes`, color: TINTA },
      { label: 'Mesa de Servicio', valor: pesos(t.mesa), sub: `${pct(t.mesa, t.total)} del total`, color: AZUL },
      { label: 'Soporte en Sitio', valor: pesos(t.sitio), sub: `${pct(t.sitio, t.total)} del total`, color: MAGENTA }
    ];
    const gap = 12;
    const cw = (W - 2 * M - gap * 2) / 3;
    tarjetas.forEach((k, i) => {
      const x = M + i * (cw + gap);
      doc.setDrawColor(...LINEA);
      doc.setLineWidth(0.8);
      doc.roundedRect(x, y, cw, 70, 6, 6, 'S');
      doc.setFillColor(...k.color);
      doc.rect(x, y, cw, 4, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...GRIS);
      doc.text(k.label, x + 12, y + 22);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(17);
      doc.setTextColor(...TINTA);
      doc.text(k.valor, x + 12, y + 44);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...GRIS);
      doc.text(k.sub, x + 12, y + 59);
    });
    y += 88;

    // Barra Mesa / Sitio
    const bw = W - 2 * M;
    const totArea = t.mesa + t.sitio + t.sinArea;
    if (totArea > 0) {
      const wm = (t.mesa / totArea) * bw;
      doc.setFillColor(...AZUL);
      doc.rect(M, y, wm, 10, 'F');
      doc.setFillColor(...MAGENTA);
      doc.rect(M + wm, y, (t.sitio / totArea) * bw, 10, 'F');
      if (t.sinArea > 0) {
        doc.setFillColor(198, 198, 198);
        doc.rect(M + wm + (t.sitio / totArea) * bw, y, (t.sinArea / totArea) * bw, 10, 'F');
      }
      doc.setFontSize(8.5);
      doc.setTextColor(...AZUL);
      doc.text(`Mesa de Servicio ${pct(t.mesa, totArea)}`, M, y + 24);
      doc.setTextColor(...MAGENTA);
      doc.text(`Soporte en Sitio ${pct(t.sitio, totArea)}`, W - M, y + 24, { align: 'right' });
      y += 44;
    }

    // Encabezados y valores en la misma alineación: texto a la izquierda,
    // cifras centradas bajo su título (también en la fila de total).
    const alinear = (centradas) => (d) => {
      if (centradas.includes(d.column.index)) d.cell.styles.halign = 'center';
    };
    const lineaInferior = (d) => {
      if (d.section === 'body') {
        doc.setDrawColor(...LINEA);
        doc.line(d.cell.x, d.cell.y + d.cell.height, d.cell.x + d.cell.width, d.cell.y + d.cell.height);
      }
    };
    const tablaPortada = {
      margin: { left: M, right: M, top: 50 },
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 5, textColor: TINTA },
      headStyles: { fontStyle: 'bold', textColor: GRIS, fillColor: [244, 244, 244] },
      footStyles: { fontStyle: 'bold', fillColor: [244, 244, 244] },
      didDrawCell: lineaInferior
    };

    // Discriminación de costos: cada concepto (Cloud y cada proveedor) partido
    // en Mesa y Sitio, para ver cuánto consume cada área de Cloud y de proveedor.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...TINTA);
    doc.text('Discriminación de costos', M, y);
    y += 6;
    const conSinArea = t.sinArea > 0.5;
    const vacio = { mesa: 0, sitio: 0, sinArea: 0, total: 0 };
    const filaConcepto = (nombre, r) => [
      nombre, pesos(r.mesa), pesos(r.sitio), ...(conSinArea ? [pesos(r.sinArea)] : []), pesos(r.total), pct(r.total, t.total)
    ];
    const composicion = [
      sinTrm
        ? ['Cloud (IBM Cloud)', { content: `${formatCurrency(t.cloud_usd)} USD · falta TRM, no incluido`, colSpan: conSinArea ? 5 : 4, styles: { textColor: [178, 34, 34] } }]
        : filaConcepto('Cloud (IBM Cloud)', liq.porConcepto.cloud || vacio),
      ...Object.keys(PROVEEDORES).filter((p) => liq.porProveedor[p]).map((p) =>
        filaConcepto(`Proveedor ${PROVEEDORES[p]}`, liq.porConcepto[p] || vacio))
    ];
    const colTotal = conSinArea ? 4 : 3;
    autoTable(doc, {
      ...tablaPortada,
      startY: y + 4,
      head: [['Concepto', 'Mesa de Servicio', 'Soporte en Sitio', ...(conSinArea ? ['Sin área'] : []), 'Total', '% del total']],
      body: composicion,
      foot: [['Total', pesos(t.mesa), pesos(t.sitio), ...(conSinArea ? [pesos(t.sinArea)] : []), pesos(t.total), '100%']],
      columnStyles: { 0: { cellWidth: conSinArea ? 115 : 135 }, [colTotal]: { fontStyle: 'bold' } },
      didParseCell: (d) => {
        if (d.column.index > 0) d.cell.styles.halign = 'center';
        // Encabezados de área con su color, para leer la matriz de un vistazo
        if (d.section === 'head' && d.column.index === 1) d.cell.styles.textColor = AZUL;
        if (d.section === 'head' && d.column.index === 2) d.cell.styles.textColor = MAGENTA;
        if (d.section === 'foot' && d.column.index === 1) d.cell.styles.textColor = AZUL;
        if (d.section === 'foot' && d.column.index === 2) d.cell.styles.textColor = MAGENTA;
      }
    });
    y = doc.lastAutoTable.finalY + 26;
    if (sinTrm) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(178, 34, 34);
      doc.text('Atención: el mes no tiene TRM; el costo Cloud no está incluido en los totales en pesos.', M, y - 8);
      doc.setTextColor(...TINTA);
      y += 10;
    }

    // Costo por área: cada área con sus aplicaciones. Las compartidas aparecen
    // en ambas con la parte que le toca a cada una (suma de las filas por cliente).
    const porArea = new Map();
    liq.clientes.forEach((c) => c.apps.forEach((a) => {
      if (!porArea.has(a.key)) porArea.set(a.key, { name: a.name, mesa: 0, sitio: 0, sinArea: 0 });
      const r = porArea.get(a.key);
      r.mesa += a.mesa; r.sitio += a.sitio; r.sinArea += a.sinArea;
    }));
    const grupos = [
      { campo: 'mesa', titulo: 'Mesa de Servicio', total: t.mesa, color: AZUL },
      { campo: 'sitio', titulo: 'Soporte en Sitio', total: t.sitio, color: MAGENTA },
      { campo: 'sinArea', titulo: 'Sin área asignada', total: t.sinArea, color: GRIS }
    ].filter((g) => g.total > 0.5);
    const filasArea = [];
    grupos.forEach((g) => {
      filasArea.push([
        { content: g.titulo, styles: { fontStyle: 'bold', textColor: g.color } },
        { content: pesos(g.total), styles: { fontStyle: 'bold', textColor: g.color } },
        { content: '100%', styles: { fontStyle: 'bold', textColor: g.color } }
      ]);
      [...porArea.entries()]
        .filter(([, r]) => r[g.campo] > 0.5)
        .sort((x, z) => z[1][g.campo] - x[1][g.campo])
        .forEach(([, r]) => filasArea.push([
          `      ${r.name}`, pesos(r[g.campo]), pct(r[g.campo], g.total)
        ]));
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...TINTA);
    doc.text('Costo por área', M, y);
    autoTable(doc, {
      ...tablaPortada,
      startY: y + 10,
      head: [['Área / aplicación', 'Valor (COP)', '% del área']],
      body: filasArea,
      foot: [['Total', pesos(t.total), '']],
      columnStyles: { 0: { cellWidth: 255 }, 1: { cellWidth: 140 } },
      didParseCell: (d) => {
        alinear([1, 2])(d);
        // La fila de cada área (su subtotal) es un encabezado: fondo azul y texto blanco
        if (d.section === 'body' && d.row.raw[0]?.content) {
          d.cell.styles.fillColor = AZUL;
          d.cell.styles.textColor = [255, 255, 255];
        }
      }
    });

    // ---- Página 2: por cliente ----
    doc.addPage();
    let y2 = encabezado('Costo por cliente');
    autoTable(doc, {
      startY: y2 + 6,
      margin: { left: M, right: M, top: 70 },
      head: [['Cliente', 'Cloud', 'Proveedor', 'Total', '% total', 'Mesa', 'Sitio']],
      body: liq.clientes.map((c) => [
        c.name, sinTrm ? '—' : pesos(c.cloud), pesos(c.proveedor), pesos(c.total), pct(c.total, t.total), pesos(c.mesa), pesos(c.sitio)
      ]),
      foot: [['Total', sinTrm ? '—' : pesos(t.cloud), pesos(t.proveedor), pesos(t.total), '100%', pesos(t.mesa), pesos(t.sitio)]],
      theme: 'striped',
      styles: { fontSize: 8.5, cellPadding: 4.5, textColor: TINTA },
      headStyles: { fillColor: AZUL, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [232, 234, 238], textColor: TINTA, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 251] },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' },
        4: { halign: 'right', textColor: GRIS }, 5: { halign: 'right', textColor: AZUL }, 6: { halign: 'right', textColor: MAGENTA }
      },
      didParseCell: (d) => {
        if ((d.section === 'head' || d.section === 'foot') && d.column.index > 0) d.cell.styles.halign = 'right';
      }
    });

    // ---- Página 3: por aplicación ----
    doc.addPage();
    y2 = encabezado('Costo por aplicación');
    autoTable(doc, {
      startY: y2 + 6,
      margin: { left: M, right: M, top: 70 },
      head: [['Aplicación', 'Proveedor', 'Área', 'Clientes', 'Cloud', 'Proveedor (COP)', 'Total']],
      body: liq.apps.map((a) => [
        a.name, a.proveedores.map((p) => PROVEEDORES[p]).join(', '), areaCorta(a.frac_mesa), a.clientes,
        sinTrm ? `${formatCurrency(a.cloud_usd)} USD` : pesos(a.cloud), pesos(a.proveedor), pesos(a.total)
      ]),
      foot: [['Total', '', '', '', sinTrm ? `${formatCurrency(t.cloud_usd)} USD` : pesos(t.cloud), pesos(t.proveedor), pesos(t.total)]],
      theme: 'striped',
      styles: { fontSize: 8.5, cellPadding: 4.5, textColor: TINTA },
      headStyles: { fillColor: AZUL, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [232, 234, 238], textColor: TINTA, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 251] },
      columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' } },
      didParseCell: (d) => {
        if ((d.section === 'head' || d.section === 'foot') && d.column.index > 2) d.cell.styles.halign = 'right';
      }
    });
    // ---- Anexo: detalle por cliente ----
    doc.addPage();
    let y3 = encabezado('Anexo · Detalle por cliente');
    // Anchos fijos para que todas las tablas del anexo queden alineadas.
    const anchoAnexo = { 0: 110, 1: 95, 2: 45, 3: 70, 4: 70, 5: 62, 6: 63 };
    liq.clientes.forEach((c) => {
      // Si no cabe el encabezado del cliente con un par de filas, va a la página siguiente.
      const alto = 34 + Math.min(c.apps.length, 3) * 17;
      if (y3 + alto > H - 50) {
        doc.addPage();
        y3 = encabezado('Anexo · Detalle por cliente');
      }
      autoTable(doc, {
        startY: y3 + 4,
        rowPageBreak: 'avoid',
        margin: { left: M, right: M, top: 70 },
        head: [
          [{ content: `${c.name}  ·  ${pesos(c.total)}`, colSpan: 7, styles: { fillColor: [232, 234, 238], textColor: TINTA, fontSize: 9.5 } }],
          ['Aplicación', 'Origen', 'Área', 'Cloud', 'Proveedor', 'Mesa', 'Sitio']
        ],
        body: c.apps.map((a) => [
          a.name, [a.origen, a.tieneCloud ? 'Cloud' : ''].filter(Boolean).join(' + '), areaCorta(a.frac),
          a.tieneCloud ? (sinTrm ? `${formatCurrency(a.cloud_usd)} USD` : pesos(a.cloud)) : '', a.proveedor ? pesos(a.proveedor) : '',
          pesos(a.mesa), pesos(a.sitio)
        ]),
        theme: 'plain',
        styles: { fontSize: 8, cellPadding: 3.5, textColor: TINTA },
        headStyles: { fontStyle: 'bold', textColor: GRIS },
        columnStyles: {
          0: { cellWidth: anchoAnexo[0] }, 1: { cellWidth: anchoAnexo[1] }, 2: { cellWidth: anchoAnexo[2] },
          3: { cellWidth: anchoAnexo[3], halign: 'right' }, 4: { cellWidth: anchoAnexo[4], halign: 'right' },
          5: { cellWidth: anchoAnexo[5], halign: 'right', textColor: AZUL }, 6: { cellWidth: anchoAnexo[6], halign: 'right', textColor: MAGENTA }
        },
        didParseCell: (d) => {
          if (d.section === 'head' && d.row.index === 1 && d.column.index > 2) d.cell.styles.halign = 'right';
        },
        didDrawCell: (d) => {
          if (d.section === 'body') {
            doc.setDrawColor(...LINEA);
            doc.line(d.cell.x, d.cell.y + d.cell.height, d.cell.x + d.cell.width, d.cell.y + d.cell.height);
          }
        }
      });
      y3 = doc.lastAutoTable.finalY + 12;
    });

    // Pie de página
    const n = doc.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...GRIS);
      doc.text(`CostProject · Generado el ${hoy()}`, M, H - 22);
      doc.text(`Página ${i} de ${n}`, W - M, H - 22, { align: 'right' });
    }

    doc.save(`Informe_Liquidacion_${liq.periodo}.pdf`);
    onSuccess?.('Informe generado', `Liquidación de ${nombreMes} en PDF`);
  } catch (error) {
    console.error('Error generando el informe PDF:', error);
    onError?.('Error', 'No se pudo generar el informe PDF');
  }
};

/* ============================== EXCEL ============================== */

const FMT_COP = '"$"#,##0';
const FMT_USD = '"US$"#,##0.00';
const FMT_PCT = '0.0%';
const ARGB = { azul: 'FF0F62FE', magenta: 'FFD02670', tinta: 'FF161616', gris: 'FF6F6F6F', fondo: 'FFF4F4F4', linea: 'FFE0E0E0', banda: 'FFF8F9FB' };

const estiloEncabezado = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.azul } };
    cell.alignment = { vertical: 'middle', wrapText: true };
    cell.border = { bottom: { style: 'thin', color: { argb: ARGB.azul } } };
  });
  row.height = 22;
};

const estiloTotal = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EAEE' } };
    cell.border = { top: { style: 'thin', color: { argb: ARGB.tinta } } };
  });
};

const titulo = (ws, texto, sub, columnas) => {
  ws.mergeCells(1, 1, 1, columnas);
  ws.getCell(1, 1).value = texto;
  ws.getCell(1, 1).font = { bold: true, size: 15, color: { argb: ARGB.tinta } };
  ws.mergeCells(2, 1, 2, columnas);
  ws.getCell(2, 1).value = sub;
  ws.getCell(2, 1).font = { size: 10, color: { argb: ARGB.gris } };
  ws.getRow(1).height = 24;
};

// Celda con fórmula y su valor ya resuelto (abre bien aunque no recalcule).
const formula = (f, result) => ({ formula: f, result });

// soloDatos: versión para el enlace de gerencia, solo cifras (sin notas,
// puntos clave ni metodología).
export const generarInformeExcel = async ({ liq, nombreMes, soloDatos = false, onSuccess, onError }) => {
  try {
    const t = liq.totales;
    const sinTrm = !liq.trm;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'CostProject';
    wb.created = new Date();
    const sub = soloDatos
      ? `${liq.trm ? `TRM ${liq.trm.toLocaleString('es-CO')}` : 'Sin TRM'} · Generado el ${hoy()}`
      : `Costo Cloud + proveedores por cliente · ${liq.trm ? `TRM ${liq.trm.toLocaleString('es-CO')}` : 'sin TRM: Cloud no incluido en pesos'} · Generado el ${hoy()}`;

    // ---- Resumen ----
    const r = wb.addWorksheet('Resumen', { views: [{ showGridLines: false }] });
    r.columns = soloDatos
      ? [{ width: 34 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 14 }]
      : [{ width: 34 }, { width: 20 }, { width: 14 }, { width: 46 }];
    titulo(r, `Informe de costos I+D · ${nombreMes}`, sub, soloDatos ? 6 : 4);
    r.addRow([]);
    estiloEncabezado(r.addRow(soloDatos ? ['Concepto', 'Valor (COP)', '% del total'] : ['Concepto', 'Valor (COP)', '% del total', 'Nota']));
    const filasResumen = [
      ['Costo total', t.total, 1, `${liq.clientes.length} clientes`],
      ['Mesa de Servicio', t.mesa, t.total ? t.mesa / t.total : 0, 'Herramientas del área Mesa + su parte de las compartidas'],
      ['Soporte en Sitio', t.sitio, t.total ? t.sitio / t.total : 0, 'Herramientas del área Sitio + su parte de las compartidas'],
      ['Cloud (IBM Cloud)', sinTrm ? null : t.cloud, sinTrm || !t.total ? null : t.cloud / t.total, `${formatCurrency(t.cloud_usd)} USD${sinTrm ? ' · falta TRM' : ''}`],
      ...Object.keys(PROVEEDORES).filter((p) => liq.porProveedor[p]).map((p) => [
        `Proveedor ${PROVEEDORES[p]}`, liq.porProveedor[p], t.total ? liq.porProveedor[p] / t.total : 0, 'Según contrato vigente'
      ])
    ];
    filasResumen.forEach((f, i) => {
      const row = r.addRow(soloDatos ? f.slice(0, 3) : f);
      row.getCell(2).numFmt = FMT_COP;
      row.getCell(3).numFmt = FMT_PCT;
      row.getCell(4).font = { color: { argb: ARGB.gris } };
      if (i === 0) row.eachCell((c) => { c.font = { ...c.font, bold: true }; });
      if (i === 1) row.getCell(1).font = { bold: true, color: { argb: ARGB.azul } };
      if (i === 2) row.getCell(1).font = { bold: true, color: { argb: ARGB.magenta } };
      row.eachCell((c) => { c.border = { bottom: { style: 'thin', color: { argb: ARGB.linea } } }; });
    });
    if (soloDatos) {
      // Discriminación: cada concepto partido en Mesa y Sitio
      const conSinArea = t.sinArea > 0.5;
      const vacio = { mesa: 0, sitio: 0, sinArea: 0, total: 0 };
      r.addRow([]);
      r.addRow(['Discriminación de costos']).getCell(1).font = { bold: true, size: 12 };
      const cab = r.addRow(['Concepto', 'Mesa de Servicio', 'Soporte en Sitio', ...(conSinArea ? ['Sin área'] : []), 'Total', '% del total']);
      estiloEncabezado(cab);
      const conceptos = [
        ...(sinTrm ? [] : [['Cloud (IBM Cloud)', liq.porConcepto.cloud || vacio]]),
        ...Object.keys(PROVEEDORES).filter((p) => liq.porProveedor[p]).map((p) => [`Proveedor ${PROVEEDORES[p]}`, liq.porConcepto[p] || vacio])
      ];
      const fila = (nombre, x) => [nombre, x.mesa, x.sitio, ...(conSinArea ? [x.sinArea] : []), x.total, t.total ? x.total / t.total : 0];
      const nCols = conSinArea ? 6 : 5;
      const formatear = (row) => {
        for (let k = 2; k < nCols; k += 1) row.getCell(k).numFmt = FMT_COP;
        row.getCell(nCols).numFmt = FMT_PCT;
        row.getCell(2).font = { ...row.getCell(2).font, color: { argb: ARGB.azul } };
        row.getCell(3).font = { ...row.getCell(3).font, color: { argb: ARGB.magenta } };
      };
      conceptos.forEach(([nombre, x]) => {
        const row = r.addRow(fila(nombre, x));
        formatear(row);
        row.eachCell((cl) => { cl.border = { bottom: { style: 'thin', color: { argb: ARGB.linea } } }; });
      });
      const totR = r.addRow(fila('Total', { mesa: t.mesa, sitio: t.sitio, sinArea: t.sinArea, total: t.total }));
      estiloTotal(totR);
      formatear(totR);

      // Costo por área y aplicación
      const porArea = { mesa: {}, sitio: {}, sinArea: {} };
      liq.clientes.forEach((cl) => cl.apps.forEach((ap) => {
        ['mesa', 'sitio', 'sinArea'].forEach((k) => {
          if (ap[k]) porArea[k][ap.name] = (porArea[k][ap.name] || 0) + ap[k];
        });
      }));
      r.addRow([]);
      r.addRow(['Costo por área']).getCell(1).font = { bold: true, size: 12 };
      estiloEncabezado(r.addRow(['Área / aplicación', 'Valor (COP)', '% del área']));
      [['mesa', 'Mesa de Servicio'], ['sitio', 'Soporte en Sitio'], ['sinArea', 'Sin área asignada']].forEach(([k, nombre]) => {
        const totalArea = k === 'sinArea' ? t.sinArea : t[k];
        if (!(totalArea > 0.5)) return;
        const g = r.addRow([nombre, totalArea, 1]);
        g.eachCell((cl) => {
          cl.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cl.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: k === 'sitio' ? ARGB.magenta : ARGB.azul } };
        });
        g.getCell(2).numFmt = FMT_COP;
        g.getCell(3).numFmt = FMT_PCT;
        Object.entries(porArea[k]).sort((x, y) => y[1] - x[1]).forEach(([app, v]) => {
          const row = r.addRow([app, v, v / totalArea]);
          row.getCell(1).alignment = { indent: 2 };
          row.getCell(2).numFmt = FMT_COP;
          row.getCell(3).numFmt = FMT_PCT;
          row.eachCell((cl) => { cl.border = { bottom: { style: 'thin', color: { argb: ARGB.linea } } }; });
        });
      });
      const totArea = r.addRow(['Total', t.total, '']);
      estiloTotal(totArea);
      totArea.getCell(2).numFmt = FMT_COP;
    }
    if (!soloDatos) {
    r.addRow([]);
    r.addRow(['Puntos clave']).getCell(1).font = { bold: true, size: 12 };
    conclusiones(liq, pesos).forEach((f) => {
      const row = r.addRow([`•  ${f}`]);
      r.mergeCells(row.number, 1, row.number, 4);
      row.getCell(1).alignment = { wrapText: true, vertical: 'top' };
      row.height = 30;
    });
    r.addRow([]);
    r.addRow(['Hojas de este libro']).getCell(1).font = { bold: true, size: 12 };
    [['Por cliente', 'Cuánto paga cada cliente: Cloud, proveedor, total y reparto Mesa / Sitio.'],
      ['Por aplicación', 'Cuánto vale cada aplicación en el mes (Cloud + proveedor).'],
      ['Detalle', 'Cada cliente por aplicación, con el origen del costo y cómo se repartió. Tiene filtros.'],
      ['Metodología', 'Cómo se calcula cada valor.']].forEach(([h, d]) => {
      const row = r.addRow([h, d]);
      r.mergeCells(row.number, 2, row.number, 4);
      row.getCell(1).font = { bold: true, color: { argb: ARGB.azul } };
      row.getCell(2).font = { color: { argb: ARGB.gris } };
    });
    }

    // ---- Por cliente ----
    const c = wb.addWorksheet('Por cliente', { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
    c.columns = [{ width: 26 }, { width: 15 }, { width: 10 }, { width: 17 }, { width: 17 }, { width: 18 }, { width: 11 }, { width: 18 }, { width: 18 }];
    titulo(c, `Costo por cliente · ${nombreMes}`, sub, 9);
    c.addRow([]);
    estiloEncabezado(c.addRow(['Cliente', 'Cloud (USD)', 'TRM', 'Cloud (COP)', 'Proveedor (COP)', 'Total (COP)', '% del total', 'Mesa de Servicio', 'Soporte en Sitio']));
    const ini = 5;
    const fin = ini + liq.clientes.length - 1;
    const filaTot = fin + 1;
    liq.clientes.forEach((cl, i) => {
      const n = ini + i;
      const row = c.addRow([
        cl.name,
        Math.round(cl.cloud_usd * 100) / 100,
        liq.trm || null,
        sinTrm ? null : formula(`B${n}*C${n}`, cl.cloud),
        cl.proveedor,
        formula(`IFERROR(D${n}+0,0)+E${n}`, cl.total),
        formula(`IFERROR(F${n}/F$${filaTot},0)`, t.total ? cl.total / t.total : 0),
        cl.mesa,
        cl.sitio
      ]);
      row.getCell(2).numFmt = FMT_USD;
      row.getCell(3).numFmt = '#,##0.00';
      row.getCell(3).font = { color: { argb: ARGB.gris } };
      [4, 5, 6, 8, 9].forEach((k) => { row.getCell(k).numFmt = FMT_COP; });
      row.getCell(7).numFmt = FMT_PCT;
      row.getCell(6).font = { bold: true };
      row.getCell(8).font = { color: { argb: ARGB.azul } };
      row.getCell(9).font = { color: { argb: ARGB.magenta } };
      if (i % 2 === 1) row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.banda } }; });
    });
    const tot = c.addRow([
      'Total',
      formula(`SUM(B${ini}:B${fin})`, t.cloud_usd),
      liq.trm || null,
      sinTrm ? null : formula(`SUM(D${ini}:D${fin})`, t.cloud),
      formula(`SUM(E${ini}:E${fin})`, t.proveedor),
      formula(`SUM(F${ini}:F${fin})`, t.total),
      formula(`SUM(G${ini}:G${fin})`, 1),
      formula(`SUM(H${ini}:H${fin})`, t.mesa),
      formula(`SUM(I${ini}:I${fin})`, t.sitio)
    ]);
    estiloTotal(tot);
    tot.getCell(2).numFmt = FMT_USD;
    tot.getCell(3).numFmt = '#,##0.00';
    [4, 5, 6, 8, 9].forEach((k) => { tot.getCell(k).numFmt = FMT_COP; });
    tot.getCell(7).numFmt = FMT_PCT;
    c.autoFilter = { from: { row: 4, column: 1 }, to: { row: fin, column: 9 } };

    // ---- Por aplicación ----
    const a = wb.addWorksheet('Por aplicación', { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
    a.columns = [{ width: 22 }, { width: 16 }, { width: 20 }, { width: 10 }, { width: 14 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 44 }];
    titulo(a, `Costo por aplicación · ${nombreMes}`, sub, 9);
    a.addRow([]);
    estiloEncabezado(a.addRow(['Aplicación', 'Proveedor', 'Área', 'Clientes', 'Cloud (USD)', 'Cloud (COP)', 'Proveedor (COP)', 'Total (COP)', 'Herramientas']));
    const iniA = 5;
    liq.apps.forEach((ap, i) => {
      const n = iniA + i;
      const row = a.addRow([
        ap.name, ap.proveedores.map((p) => PROVEEDORES[p]).join(', '), areaTexto(ap.frac_mesa), ap.clientes,
        Math.round(ap.cloud_usd * 100) / 100, sinTrm ? null : ap.cloud, ap.proveedor,
        formula(`IFERROR(F${n},0)+G${n}`, ap.total), ap.herramientas.join(', ')
      ]);
      row.getCell(5).numFmt = FMT_USD;
      [6, 7, 8].forEach((k) => { row.getCell(k).numFmt = FMT_COP; });
      row.getCell(8).font = { bold: true };
      row.getCell(9).font = { color: { argb: ARGB.gris } };
      if (i % 2 === 1) row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.banda } }; });
    });
    const finA = iniA + liq.apps.length - 1;
    const totA = a.addRow(['Total', '', '', '', formula(`SUM(E${iniA}:E${finA})`, t.cloud_usd), sinTrm ? null : formula(`SUM(F${iniA}:F${finA})`, t.cloud),
      formula(`SUM(G${iniA}:G${finA})`, t.proveedor), formula(`SUM(H${iniA}:H${finA})`, t.total), '']);
    estiloTotal(totA);
    totA.getCell(5).numFmt = FMT_USD;
    [6, 7, 8].forEach((k) => { totA.getCell(k).numFmt = FMT_COP; });

    // ---- Detalle ----
    const d = wb.addWorksheet('Detalle', { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
    const colsDetalle = soloDatos ? 9 : 10;
    d.columns = [{ width: 24 }, { width: 20 }, { width: 18 }, { width: 14 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 50 }].slice(0, colsDetalle);
    titulo(d, `Detalle por cliente y aplicación · ${nombreMes}`, sub, colsDetalle);
    d.addRow([]);
    estiloEncabezado(d.addRow(['Cliente', 'Aplicación', 'Área', 'Cloud (USD)', 'Cloud (COP)', 'Proveedor (COP)', 'Total (COP)', 'Mesa de Servicio', 'Soporte en Sitio', 'Cómo se repartió'].slice(0, colsDetalle)));
    let filasDetalle = 0;
    liq.clientes.forEach((cl) => cl.apps.forEach((ap) => {
      const row = d.addRow([
        cl.name, ap.name, areaTexto(ap.frac),
        ap.tieneCloud ? Math.round(ap.cloud_usd * 100) / 100 : null,
        ap.tieneCloud && !sinTrm ? ap.cloud : null,
        ap.proveedor || null,
        ap.total, ap.mesa, ap.sitio,
        [ap.notaProveedor, ap.notaCloud].filter(Boolean).join(' · ')
      ].slice(0, colsDetalle));
      row.getCell(4).numFmt = FMT_USD;
      [5, 6, 7, 8, 9].forEach((k) => { row.getCell(k).numFmt = FMT_COP; });
      row.getCell(7).font = { bold: true };
      if (!soloDatos) row.getCell(10).font = { color: { argb: ARGB.gris } };
      filasDetalle += 1;
    }));
    d.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4 + filasDetalle, column: colsDetalle } };

    // ---- Metodología ----
    if (!soloDatos) {
    const m = wb.addWorksheet('Metodología', { views: [{ showGridLines: false }] });
    m.columns = [{ width: 32 }, { width: 100 }];
    titulo(m, 'Cómo se calcula', `Liquidación de ${nombreMes}`, 2);
    m.addRow([]);
    METODOLOGIA.forEach(([h, txt]) => {
      const row = m.addRow([h, txt]);
      row.getCell(1).font = { bold: true };
      row.getCell(2).alignment = { wrapText: true, vertical: 'top' };
      row.getCell(1).alignment = { vertical: 'top' };
      row.height = 48;
    });
    m.addRow([]);
    const contratos = (liq.contratos || []).map((k) => `${PROVEEDORES[k.proveedor]}${k.referencia ? ` (${k.referencia})` : ''}`).join(', ');
    m.addRow(['Contratos vigentes', contratos || 'Ninguno']).getCell(1).font = { bold: true };
    m.addRow(['TRM del mes', liq.trm || 'Sin TRM']).getCell(1).font = { bold: true };
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = soloDatos ? `Informe_Costos_ID_${liq.periodo}.xlsx` : `Liquidacion_${liq.periodo}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    onSuccess?.('Excel generado', `Liquidación de ${nombreMes}`);
  } catch (error) {
    console.error('Error generando el Excel de liquidación:', error);
    onError?.('Error', 'No se pudo generar el Excel');
  }
};
